/**
 * useItemReviews — fetches an item's public rating summary + first page of
 * public reviews (M4). Backs the "Reviews" section on ItemDetailScreen.
 *
 * Degrades to an empty state on any fetch failure — a reviews outage must
 * never block or error the item detail screen, so no error is thrown or
 * surfaced to the caller.
 */
import { useEffect, useState } from "react";

import { reviewApi } from "@/api/reviewApi";
import type { ItemReviewSummary, PublicReview } from "@/api/reviewApi";

export type UseItemReviewsResult = {
  summary: ItemReviewSummary | null;
  reviews: PublicReview[];
  loading: boolean;
  loadingMore: boolean;
  nextPage: number | null;
  loadMore: () => void;
};

export function useItemReviews(itemId: string | undefined): UseItemReviewsResult {
  const [summary, setSummary] = useState<ItemReviewSummary | null>(null);
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [nextPage, setNextPage] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!itemId) {
      setSummary(null);
      setReviews([]);
      setNextPage(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const [summaryRes, pageRes] = await Promise.all([
          reviewApi.getItemReviewsSummary(itemId),
          reviewApi.getItemPublicReviews(itemId, 1),
        ]);
        if (cancelled) return;
        setSummary(summaryRes ?? null);
        setReviews(pageRes?.items ?? []);
        setNextPage(pageRes?.nextPage ?? null);
      } catch {
        // Reviews are a nice-to-have surface — silently degrade to empty.
        if (!cancelled) {
          setSummary(null);
          setReviews([]);
          setNextPage(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const loadMore = () => {
    if (!itemId || nextPage == null || loadingMore) return;
    setLoadingMore(true);
    reviewApi
      .getItemPublicReviews(itemId, nextPage)
      .then((pageRes) => {
        setReviews((prev) => [...prev, ...(pageRes?.items ?? [])]);
        setNextPage(pageRes?.nextPage ?? null);
      })
      .catch(() => {
        // Stop paging silently — what's already loaded stays visible.
        setNextPage(null);
      })
      .finally(() => setLoadingMore(false));
  };

  return { summary, reviews, loading, loadingMore, nextPage, loadMore };
}
